// schedule + confirm reception dialog — single component with mode prop
import { Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import { faCalendarAlt, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../contexts/ThemeContext";
import BaseDialog from "../ui/BaseDialog";
import ActionButton from "../ui/ActionButton";
import Alert from "../ui/Alert";
import {
  formatQuantity,
  formatDate,
  getDonorScheduleYMD,
  getDonorTimeWindow,
} from "../../utils/scheduleUtils";

const MODE_CONFIG = {
  schedule: {
    icon: { icon: faCalendarAlt, variant: "info" },
    titleKey: "schedule.dialogs.schedule.title",
    titleFallback: "Confirm pickup",
    subtitleKey: "schedule.dialogs.schedule.subtitle",
    subtitleFallback: "Review the donor-provided pickup details below. Confirming will accept this information as-is.",
    confirmVariant: "primary",
    confirmKey: "schedule.actions.schedule",
    confirmFallback: "Confirm pickup",
    dateHeaderKey: "schedule.dialogs.schedule.pickupDate",
    dateHeaderFallback: "Pickup Date",
  },
  confirm: {
    icon: { icon: faExclamationTriangle, variant: "danger" },
    titleKey: "schedule.dialogs.confirm.title",
    titleFallback: "Confirm pickup reception",
    subtitleKey: "schedule.dialogs.confirm.subtitle",
    subtitleFallback: "Are you sure you want to confirm reception of these pickups? This action confirms that the materials have been successfully received and cannot be undone.",
    confirmVariant: "danger",
    confirmKey: "schedule.dialogs.confirm.confirm",
    confirmFallback: "Yes, confirm reception",
    confirmingKey: "schedule.dialogs.confirm.confirming",
    confirmingFallback: "Confirming...",
    dateHeaderKey: "schedule.table.scheduledDate",
    dateHeaderFallback: "Scheduled Date",
  },
};

export default function ScheduleActionDialog({
  mode = "schedule",
  open,
  onClose,
  onConfirm,
  submitting,
  error,
  selectedRows,
  confirmDisabled,
  // schedule-mode extras
  hasMismatchedDates,
  selectedDates,
}) {
  const { t } = useTranslation();
  const { darkMode } = useDarkMode();
  const cfg = MODE_CONFIG[mode];
  const isSchedule = mode === "schedule";

  const confirmLabel = submitting && !isSchedule
    ? t(cfg.confirmingKey, cfg.confirmingFallback)
    : t(cfg.confirmKey, cfg.confirmFallback);

  return (
    <BaseDialog
      open={open}
      onClose={() => !submitting && onClose()}
      darkMode={darkMode}
      size="lg"
      title={t(cfg.titleKey, cfg.titleFallback)}
      icon={cfg.icon}
      actions={
        <>
          <ActionButton variant="default" modal onClick={onClose} disabled={submitting}>
            {t("schedule.actions.cancel", "Cancel")}
          </ActionButton>
          <ActionButton
            variant={cfg.confirmVariant}
            modal
            onClick={onConfirm}
            disabled={confirmDisabled}
            loading={submitting}
          >
            {confirmLabel}
          </ActionButton>
        </>
      }
    >
      <div className="schedule-dialog-body">
        {error && <Alert message={error} severity="error" />}

        <p className="schedule-dialog-desc">
          {t(cfg.subtitleKey, cfg.subtitleFallback)}
        </p>

        {/* schedule-mode alerts */}
        {isSchedule && hasMismatchedDates && (
          <Alert
            severity="warning"
            message={t(
              "schedule.dialogs.schedule.mismatch",
              "Selected rows have different pickup dates ({{dates}}). Each row will be scheduled with its own donor-provided date.",
              { dates: selectedDates?.join(", ") }
            )}
          />
        )}

        {isSchedule && !hasMismatchedDates && selectedDates?.length === 1 && (
          <Alert
            severity="info"
            message={t(
              "schedule.dialogs.schedule.matchingDate",
              "All selected rows share the same pickup date: {{date}}",
              { date: selectedDates[0] }
            )}
          />
        )}

        {/* confirm-mode warning */}
        {!isSchedule && (
          <Alert
            severity="warning"
            message={t(
              "schedule.dialogs.confirm.warning",
              "Once confirmed, these pickups will be marked as delivered."
            )}
          />
        )}

        <div className="schedule-dialog-table-wrap">
          <Table size="small" className="schedule-dialog-table">
            <TableHead>
              <TableRow>
                <TableCell><strong>{t("schedule.table.materialType", "Material Type")}</strong></TableCell>
                <TableCell><strong>{t("schedule.table.donor", "Donor")}</strong></TableCell>
                <TableCell><strong>{t(cfg.dateHeaderKey, cfg.dateHeaderFallback)}</strong></TableCell>
                <TableCell><strong>{t("schedule.table.time", "Time Window")}</strong></TableCell>
                <TableCell align="right"><strong>{t("schedule.table.requestedQty", "Requested Qty")}</strong></TableCell>
                <TableCell align="right"><strong>{t("schedule.table.allocatedQty", "Allocated Qty")}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(selectedRows || []).map((row) => (
                <TableRow key={row.requestId || row.id}>
                  <TableCell>{row.material}</TableCell>
                  <TableCell>{row.donor}</TableCell>
                  <TableCell>
                    {isSchedule ? (getDonorScheduleYMD(row) || "—") : formatDate(row.scheduledDate)}
                  </TableCell>
                  <TableCell>
                    {isSchedule ? (getDonorTimeWindow(row) || "—") : (row.timeWindow || "—")}
                  </TableCell>
                  <TableCell align="right">{formatQuantity(row.requestedQty)}</TableCell>
                  <TableCell align="right">{formatQuantity(row.allocatedQty)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* schedule-mode missing info alert */}
        {isSchedule && confirmDisabled && selectedRows?.length > 0 && (
          <Alert
            severity="warning"
            message={t(
              "schedule.dialogs.schedule.missingInfo",
              "One or more selected rows are missing a donor pickup date/time window, or are already scheduled."
            )}
          />
        )}
      </div>
    </BaseDialog>
  );
}
